import { getCustomOrder, isNumeric, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getEnvFlags } from '../env';
import { ChartType, ColumnSortCategory, ColumnSortConfigOptions, } from '../types';
import { getSortFormatFromColumn } from './chart-model-utils';
function sortForHandlingNull(data, isAscendingSort, isSeriesCalculation) {
    const nullValues = [];
    const nonNullValues = [];
    data.forEach(value => {
        if (value === null) {
            if (isSeriesCalculation) {
                nullValues.push('{Null}');
            }
            else {
                nullValues.push(value);
            }
        }
        else {
            nonNullValues.push(value);
        }
    });
    const sortedData = isAscendingSort
        ? nullValues.concat(nonNullValues)
        : nonNullValues.concat(nullValues);
    return sortedData;
}
export const resolveCustomOrderCasing = (customOrder, dataValues) => {
    if (_.isEmpty(customOrder) || _.isEmpty(dataValues)) {
        return customOrder;
    }
    const exactValues = new Set();
    const actualByFoldedValue = new Map();
    dataValues.forEach(value => {
        if (_.isNil(value)) {
            return;
        }
        const actual = String(value);
        exactValues.add(actual);
        const folded = actual.toLowerCase();
        if (!actualByFoldedValue.has(folded)) {
            actualByFoldedValue.set(folded, actual);
        }
    });
    return customOrder.map(entry => {
        if (_.isNil(entry)) {
            return entry;
        }
        const key = String(entry);
        if (exactValues.has(key)) {
            return entry;
        }
        const actual = actualByFoldedValue.get(key.toLowerCase());
        return _.isUndefined(actual) ? entry : actual;
    });
};
export const getVizColumnsSortOrder = (columns, sortInfo, data, isSeriesCalculation, isSortV2FeatureEnabled) => {
    const isSortV2Enabled = isSortV2FeatureEnabled !== null && isSortV2FeatureEnabled !== void 0 ? isSortV2FeatureEnabled : getEnvFlags().isSortV2Enabled;
    const transformNumericStringArrayToNumericArray = (stringArray) => {
        return stringArray === null || stringArray === void 0 ? void 0 : stringArray.map(str => {
            const num = parseFloat(str);
            return _.isNaN(num) ? str : num;
        });
    };
    return columns.map(column => {
        var _a;
        const columnSortInfo = _.find(sortInfo, sortOrder => sortOrder.columnId === column.column.id);
        if (!columnSortInfo && !isSortV2Enabled) {
            return {
                sortedDataValues: [],
                columnId: column.column.id,
            };
        }
        const columnData = data.find(ele => ele.columnId === column.column.id);
        let isAscendingSort = columnSortInfo === null || columnSortInfo === void 0 ? void 0 : columnSortInfo.sortAscending;
        let columnCustomOrder = getCustomOrder(column.column) || [];
        if (isSortV2Enabled) {
            if ((columnSortInfo === null || columnSortInfo === void 0 ? void 0 : columnSortInfo.category) === ColumnSortCategory.Alpha) {
                columnCustomOrder = [];
            }
            if ([ColumnSortCategory.None].includes(columnSortInfo === null || columnSortInfo === void 0 ? void 0 : columnSortInfo.category)) {
                return {
                    sortedDataValues: [],
                    columnId: column.column.id,
                };
            }
            isAscendingSort = _.isUndefined(columnSortInfo === null || columnSortInfo === void 0 ? void 0 : columnSortInfo.sortAscending)
                ? true
                : columnSortInfo === null || columnSortInfo === void 0 ? void 0 : columnSortInfo.sortAscending;
            if ((columnSortInfo === null || columnSortInfo === void 0 ? void 0 : columnSortInfo.category) === ColumnSortCategory.Custom &&
                ((_a = columnSortInfo === null || columnSortInfo === void 0 ? void 0 : columnSortInfo.customOrder) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                columnCustomOrder = columnSortInfo.customOrder;
            }
        }
        columnCustomOrder = resolveCustomOrderCasing(columnCustomOrder, columnData === null || columnData === void 0 ? void 0 : columnData.dataValue);
        const columnDataLeftInCustomOrder = columnData
            ? new Set(columnData.dataValue
                .filter(value => !columnCustomOrder.includes(String(value))))
            : [];
        if (isNumeric(column.column)) {
            columnCustomOrder = transformNumericStringArrayToNumericArray(columnCustomOrder);
        }
        if (!_.isNil(columnSortInfo) ||
            (!_.isEmpty(columnCustomOrder) && isSortV2Enabled)) {
            if (getSortFormatFromColumn(column.column) ===
                ColumnSortConfigOptions.Custom ||
                (isSortV2Enabled &&
                    (columnSortInfo === null || columnSortInfo === void 0 ? void 0 : columnSortInfo.category) === ColumnSortCategory.Custom)) {
                return {
                    sortedDataValues: isAscendingSort
                        ? [...columnCustomOrder, ...columnDataLeftInCustomOrder]
                        : sortForHandlingNull(isSortV2Enabled
                            ? [
                                ...columnCustomOrder.slice().reverse(),
                                ...columnDataLeftInCustomOrder,
                            ]
                            : [
                                ...columnDataLeftInCustomOrder,
                                ...columnCustomOrder.slice().reverse(),
                            ], false, false),
                    columnId: column.column.id,
                };
            }
            return {
                sortedDataValues: sortForHandlingNull((columnData === null || columnData === void 0 ? void 0 : columnData.dataValue) || [], isAscendingSort, isSeriesCalculation),
                columnId: column.column.id,
            };
        }
        return {
            sortedDataValues: [],
            columnId: column.column.id,
        };
    });
};
export const getCustomOrderInSequence = (columnIds, customSortOrder, uniqueLegendValues) => {
    let customOrderSequence = [];
    let newCustomOrder = _.cloneDeep(customSortOrder);
    if (uniqueLegendValues) {
        const uniqueColumnIds = uniqueLegendValues === null || uniqueLegendValues === void 0 ? void 0 : uniqueLegendValues.map(legend => {
            return legend === null || legend === void 0 ? void 0 : legend.map(x => x.columnEffectiveId);
        });
        customOrderSequence = _.flattenDeep(uniqueColumnIds);
        newCustomOrder = newCustomOrder.filter(column => customOrderSequence.includes(column.columnId));
    }
    else {
        customOrderSequence = columnIds;
    }
    const sortBySequence = (columnsOrder, sequence) => {
        columnsOrder.sort((a, b) => {
            const nameA = a.columnId;
            const nameB = b.columnId;
            const indexA = sequence.indexOf(nameA);
            const indexB = sequence.indexOf(nameB);
            if (indexA === -1) {
                return 1;
            }
            if (indexB === -1) {
                return -1;
            }
            return indexA - indexB;
        });
    };
    sortBySequence(newCustomOrder, customOrderSequence);
    return newCustomOrder;
};
export const getCustomOrderInSequenceWithoutColumnId = (columnsIds, customSortOrder, uniqueLegendValues) => {
    return getCustomOrderInSequence(columnsIds, customSortOrder, uniqueLegendValues).map(ele => ele.sortedDataValues);
};
export const getCustomOrderInSequenceWithColumnId = (columnIds, customSortOrder, uniqueLegendValues) => {
    return getCustomOrderInSequence(columnIds, customSortOrder, uniqueLegendValues).map(ele => {
        return {
            sortedDataValues: ele.sortedDataValues,
            columnId: ele.columnId,
        };
    });
};
export const sortWithCustomOrder = (data, customSortOrder, orderOfSorting) => {
    const createCustomOrderMaps = customOrders => {
        const maps = [];
        customOrders === null || customOrders === void 0 ? void 0 : customOrders.forEach(arr => {
            const map = new Map();
            arr === null || arr === void 0 ? void 0 : arr.forEach((ele, col) => map.set(ele, col));
            maps.push(map);
        });
        return maps;
    };
    const customOrderMaps = createCustomOrderMaps(customSortOrder);
    orderOfSorting.reverse().forEach(col => {
        data.sort((a, b) => {
            const val1 = a[col];
            const val2 = b[col];
            if (!customOrderMaps[col].has(val1) &&
                !customOrderMaps[col].has(val2)) {
                return 0;
            }
            if (!customOrderMaps[col].has(val1)) {
                return 1;
            }
            if (!customOrderMaps[col].has(val2)) {
                return -1;
            }
            const index1 = customOrderMaps[col].get(val1);
            const index2 = customOrderMaps[col].get(val2);
            if (index1 < index2) {
                return -1;
            }
            if (index1 > index2) {
                return 1;
            }
            return 0;
        });
    });
};
export const customSortOnSeriesData = (orderedSeriesNames, customSortOrder, orderOfSorting) => {
    let customOrders = _.cloneDeep(customSortOrder);
    customOrders = customOrders.map(order => {
        return order.map(value => String(value));
    });
    const transformSeriesNames = seriesNames => {
        const transformedArray = [];
        for (let i = 0; i < seriesNames.length; i++) {
            const splitArray = seriesNames[i].split(', ');
            transformedArray.push(splitArray);
        }
        return transformedArray;
    };
    const reverseTransformationOfSeriesNames = seriesNames => {
        const reversedArray = [];
        for (let i = 0; i < seriesNames.length; i++) {
            const joinedString = seriesNames[i].join(', ');
            reversedArray.push(joinedString);
        }
        return reversedArray;
    };
    const seriesData = transformSeriesNames(orderedSeriesNames);
    sortWithCustomOrder(seriesData, customOrders, orderOfSorting);
    return reverseTransformationOfSeriesNames(seriesData);
};
export const filterColumnIdsForSeries = (uniqueLegendValues, columnIds) => {
    const customOrderSequence = uniqueLegendValues === null || uniqueLegendValues === void 0 ? void 0 : uniqueLegendValues.map(legend => {
        return legend === null || legend === void 0 ? void 0 : legend.map(x => x.columnEffectiveId);
    });
    const customOrderSequenceFlatten = _.flattenDeep(customOrderSequence);
    return columnIds.filter(columnId => customOrderSequenceFlatten.includes(columnId));
};
export const isCustomSortOrderApplied = (columns) => {
    return columns.some(column => {
        return (getSortFormatFromColumn(column.column) ===
            ColumnSortConfigOptions.Custom);
    });
};
export const isCustomSortSupportedForChartType = (chartType) => {
    return ![
        ChartType.Pareto,
        ChartType.GeoArea,
        ChartType.GeoBubble,
        ChartType.GeoHeatmap,
        ChartType.Kpi,
        ChartType.PivotTable,
        ChartType.Treemap,
        ChartType.SpiderWeb,
        ChartType.Candlestick,
        ChartType.Sankey,
    ].includes(chartType);
};
//# sourceMappingURL=custom-sort-utils.js.map